import ActionCopy from './copy'
import { ClientDetails, FanAIUserType } from 'types'
import { Dispatch } from 'redux'
import { getDocument } from 'helpers/auth'
import { symbols } from '@fanai/redux-request-middleware'
import {
  addClient,
  addFanaiSecurityGroup,
  addUser,
  deleteClients,
  deleteFanaiUserSecurityGroup,
  deleteUser,
  getAllClients,
  getGlobalPermissions,
  sendEmailReset
} from 'helpers/admin'
import { compare2Things, FANAI_USER_TYPE_UPDATE } from './types'

import {
  CLIENT_CREATED,
  CLIENT_UPDATED,
  CLIENTS_DELETED,
  EMAIL_RESET_LINK_SENT,
  GLOBAL_PERMISSIONS_AVAILABLE,
  RESET_SELECTED_ENTITIES,
  UPDATE_CLIENT_DELETION_STATUS,
  UPDATE_USER_PREVIOUS_PATH,
  USER_CREATED,
  USER_DELETED,
  USER_EDITED
} from '../constants'

const { FULFILLED, REJECTED, REQUEST } = symbols
const { RESET_UNIVERSE_ENTITIES } = compare2Things

export const addUserAccount = ({ client, email, id, firstName, lastName }) => {
  return async (dispatch: Dispatch<any>): Promise<any> => {
    return new Promise((resolve, reject) => {
      dispatch({
        payload: {
          fetch: () => addUser({ client, email, id, firstName, lastName }),
          lifecycle: {
            [FULFILLED]: {
              payload: res => ({ user: res }),
              type: id ? USER_EDITED : USER_CREATED
            },
            [REJECTED]: true
          },
          statusCodes: new Map([
            [
              [403],
              {
                payload: {
                  error: {
                    description: ActionCopy.get(
                      'errors.permissions.description'
                    ),
                    reason: ActionCopy.get('errors.reason'),
                    title: ActionCopy.get('errors.permissions.title')
                  }
                },
                type: 'REQUEST_ERROR'
              }
            ],
            [
              [409],
              {
                payload: {
                  error: {
                    description: 'Clients and/or user already exists',
                    reason: ActionCopy.get('errors.reason'),
                    title:
                      'Unable to create new clients and/or users at this time'
                  }
                },
                type: 'REQUEST_ERROR'
              }
            ]
          ])
        },
        type: REQUEST
      })
        // @ts-ignore
        .then(resolve)
        .catch(reject)
    })
  }
}

export const deleteClientUser = user => {
  return async (dispatch: Dispatch<any>): Promise<any> => {
    return new Promise((resolve, reject) => {
      dispatch({
        payload: {
          fetch: () => deleteUser(user),
          lifecycle: {
            [FULFILLED]: {
              payload: res => ({ user: res }),
              type: USER_DELETED
            },
            REJECTED: true
          },
          statusCodes: new Map([
            [
              [403],
              {
                payload: {
                  error: {
                    description: ActionCopy.get(
                      'errors.permissions.description'
                    ),
                    reason: ActionCopy.get('errors.reason'),
                    title: ActionCopy.get('errors.permissions.title')
                  }
                },
                type: 'REQUEST_ERROR'
              }
            ],
            [
              [400],
              {
                payload: {
                  error: {
                    description: 'Unable to delete client',
                    reason: ActionCopy.get('errors.reason'),
                    title: 'Unable to delete user at this time'
                  }
                },
                type: 'REQUEST_ERROR'
              }
            ]
          ])
        },
        type: REQUEST
      })
        // @ts-ignore
        .then(resolve)
        .catch(reject)
    })
  }
}

export const addClientAccount = ({ name, permissions, type, id, entityId }) => {
  return async (dispatch: Dispatch<any>, getState) => {
    const globalPermissions = getState().adminState.permissions || {}
    const newPermissions = {}
    // merge global permissions on the client
    Object.keys(globalPermissions).forEach(k => {
      if (permissions[k]) {
        newPermissions[k] = { ...globalPermissions[k] }
      }
    })

    let client: ClientDetails = {
      name,
      permissions: newPermissions,
      type,
      ...(entityId && { entityId })
    }

    // if id then this is an existing client so merge existing properties
    if (id) {
      const { users = [], ...rest } = getState().adminState.clients[id]
      client = {
        ...rest,
        ...client,
        id
      }
    }

    const p = addClient(client)

    dispatch({
      payload: {
        fetch: () => p,
        lifecycle: {
          [FULFILLED]: {
            payload: res => ({ client: res }),
            type: id ? CLIENT_UPDATED : CLIENT_CREATED
          },
          [REJECTED]: true
        },
        statusCodes: new Map([
          [
            [403],
            {
              payload: {
                error: {
                  description: ActionCopy.get('errors.permissions.description'),
                  reason: ActionCopy.get('errors.reason'),
                  title: ActionCopy.get('errors.permissions.title')
                }
              },
              type: 'REQUEST_ERROR'
            }
          ],
          [
            [409],
            {
              payload: {
                error: {
                  description: 'Clients and/or user already exists',
                  reason: ActionCopy.get('errors.reason'),
                  title:
                    'Unable to create new clients and/or users at this time'
                }
              },
              type: 'REQUEST_ERROR'
            }
          ],
          [
            [400],
            {
              payload: {
                error: {
                  description: 'Unable to update permissions',
                  reason:
                    'This is an unexpected error. This error has been reported.',
                  title: 'Something Went Wrong'
                }
              },
              type: 'REQUEST_ERROR'
            }
          ]
        ])
      },
      type: REQUEST
    })

    return p
  }
}

export const fetchAllClients = () => {
  return async (dispatch: Dispatch<any>) => {
    return dispatch({
      payload: {
        fetch: () => getAllClients(),
        lifecycle: {
          [FULFILLED]: {
            payload: res => res || [],
            type: 'CLIENTS_AND_USERS_AVAILABLE'
          },
          [REJECTED]: true
        },
        statusCodes: new Map([
          [
            [403],
            {
              payload: {
                error: {
                  description: ActionCopy.get('errors.permissions.description'),
                  reason: ActionCopy.get('errors.reason'),
                  title: ActionCopy.get('errors.permissions.title')
                }
              },
              type: 'REQUEST_ERROR'
            }
          ],
          [
            [404],
            {
              payload: {
                error: {
                  description: 'Clients and/or users not found',
                  reason: ActionCopy.get('errors.reason'),
                  title: 'Unable to retrieve clients and/or users at this time'
                }
              },
              type: 'REQUEST_ERROR'
            }
          ]
        ])
      },
      type: REQUEST
    })
  }
}

export const sendPasswordResetEmail = (email: string) => {
  return async (dispatch: Dispatch<any>) => {
    const req = sendEmailReset(email)
    req.then(res => {
      dispatch({
        payload: res,
        type: EMAIL_RESET_LINK_SENT
      })
    })

    return req
  }
}

export const getPermissions = () => {
  return async (dispatch: Dispatch<any>) => {
    getGlobalPermissions().then(res => {
      dispatch({
        payload: res,
        type: GLOBAL_PERMISSIONS_AVAILABLE
      })
    })
  }
}

export const markForDeletion = (clientId: string) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch({
      payload: clientId,
      type: UPDATE_CLIENT_DELETION_STATUS
    })
  }
}

export const removeClients = (clients: []) => {
  return async (dispatch: Dispatch<any>) => {
    return new Promise((resolve, reject) => {
      dispatch({
        payload: {
          fetch: () => deleteClients(clients),
          lifecycle: {
            [FULFILLED]: {
              payload: () => ({ clients }),
              type: CLIENTS_DELETED
            },
            [REJECTED]: true
          },
          statusCodes: new Map([
            [
              [403],
              {
                payload: {
                  error: {
                    description: ActionCopy.get(
                      'errors.permissions.description'
                    ),
                    reason: ActionCopy.get('errors.reason'),
                    title: ActionCopy.get('errors.permissions.title')
                  }
                },
                type: 'REQUEST_ERROR'
              }
            ],
            [
              [400],
              {
                payload: {
                  error: {
                    description:
                      'Unable to add delete client(s) and/or user(s)',
                    reason:
                      'This is an unexpected error. This error has been reported.',
                    title: 'Something Went Wrong'
                  }
                },
                type: 'REQUEST_ERROR'
              }
            ]
          ])
        },
        type: REQUEST
      })
        // @ts-ignore
        .then(resolve)
        .catch(reject)
    })
  }
}

export const setClientUser = id => {
  return async (dispatch: Dispatch<any>) => {
    return getDocument('clients', id).then(response => {
      dispatch({
        payload: response,
        type: 'UPDATE_CLIENT_DOC'
      })

      // reset the universe entities so that next time they visit a dashboard/tool, we re-retrieve the universe
      dispatch({
        type: RESET_UNIVERSE_ENTITIES
      })

      // also reset the selected entities (entity1 & entity2)
      dispatch({
        type: RESET_SELECTED_ENTITIES
      })
    })
  }
}

/**
 * Action that will add the previous path of the user into state.appState.userPreviousPath
 *
 * This is used for when the user is logged out, and attempts to go to a dashboard. We're storing
 * the path they attempted to go to before being stopped by our UnAuthenticated component
 *
 * @param {string} path
 * @returns
 */
export const setUserPreviousPath = (path: string) => {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      payload: path,
      type: UPDATE_USER_PREVIOUS_PATH
    })
  }
}

/**
 * Action that will perform the operation based off the 'type'
 * will pass payload once completed or update global errors for modal error
 *
 * @param {('FANAI_USER_TYPE_UPDATE' | 'FANAI_USER_TYPE_ADDED')} type
 * @param {FanAIUserType} fanaiUserType
 * @param {string} userId
 * @returns
 */
export const updateFanaiUserSecurityGroup = (
  type: 'FANAI_USER_TYPE_UPDATE' | 'FANAI_USER_TYPE_ADDED',
  fanaiUserType: FanAIUserType,
  clientId: string,
  userId: string
) => {
  return (dispatch: Dispatch<any>, getState) => {
    return dispatch({
      payload: {
        fetch: () =>
          type === FANAI_USER_TYPE_UPDATE
            ? deleteFanaiUserSecurityGroup(fanaiUserType, userId)
            : addFanaiSecurityGroup(fanaiUserType, userId),
        lifecycle: {
          [FULFILLED]: {
            payload: { userId, fanaiUserType, clientId },
            type
          },
          [REJECTED]: true
        },
        statusCodes: new Map([
          [
            [403],
            {
              payload: {
                error: {
                  description: ActionCopy.get('errors.permissions.description'),
                  reason: ActionCopy.get('errors.reason'),
                  title: ActionCopy.get('errors.permissions.title')
                }
              },
              type: 'REQUEST_ERROR'
            }
          ],
          [
            [404],
            {
              payload: {
                error: {
                  description: ActionCopy.get(
                    'errors.userNotFound.description'
                  ),
                  reason: ActionCopy.get('errors.reason'),
                  title: ActionCopy.get('errors.userNotFound.title')
                }
              },
              type: 'REQUEST_ERROR'
            }
          ]
        ])
      },
      type: REQUEST
    })
  }
}
